import { useRef, useState } from 'react';
import { updateDoc } from 'firebase/firestore';
import { AlertTriangle, Camera, CheckCircle2, ImageUp, Loader2 } from 'lucide-react';
import type { ProjectReader } from '../lib/types';
import { useAuth } from '../hooks/useAuth';
import { prepareImage } from '../lib/image';
import { requestNativeCamera } from '../lib/nativeBridge';
import { captureDoc, createCapture, newCaptureId, rejectCapture } from '../services/captures';
import { uploadCaptureImage } from '../services/storage';
import { newReaderId, saveLearnedReader, setProjectReaders } from '../services/readers';
import type { LearnedReader } from '../services/readings';
import { learnReaderFromPhoto } from '../services/readings';
import { Badge, Button, Input, Modal } from './ui';

/**
 * Setting up a reader: a name and one trial photo.
 *
 * The photo does the rest — BlisxBench reads it to learn the columns, the units
 * and whether the sheet is raw readings or averages with SD. What it read is
 * kept on the project, unsaved, because the trial photo is usually the first
 * real result; the person reviews it now or later, or discards it.
 */
type Step =
  | { kind: 'name' }
  | { kind: 'working'; phase: string }
  | { kind: 'failed'; message: string }
  | { kind: 'learned'; captureId: string; learned: LearnedReader }
  | { kind: 'saved'; captureId: string; learned: LearnedReader };

export default function NewReaderSetup({
  open,
  onClose,
  projectId,
  chosen,
  onReview,
  addToProject = true,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Readers already on the project; the new one is added after them. */
  chosen: ProjectReader[];
  /** Open the trial photo's readings for review. */
  onReview: (captureId: string, learned: LearnedReader) => void;
  /**
   * False on an older project, which has no reader list: the reader is saved
   * to the person's own readers only.
   */
  addToProject?: boolean;
}) {
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const [name, setName] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'name' });
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  function reset() {
    setName('');
    setStep({ kind: 'name' });
  }

  function close() {
    // A learned reader that was never saved leaves nothing half-made behind.
    if (step.kind === 'learned') void rejectCapture(projectId, step.captureId).catch(() => {});
    reset();
    onClose();
  }

  async function openCamera() {
    const result = await requestNativeCamera();
    if (!result.granted) {
      setStep({
        kind: 'failed',
        message: 'Camera access is off for BlisxBench. Turn it on in settings, or upload an image.',
      });
      return;
    }
    cameraRef.current?.click();
  }

  async function learnFrom(file: File) {
    if (!navigator.onLine) {
      setStep({ kind: 'failed', message: 'Setting up a reader needs a connection. Try again once online.' });
      return;
    }
    const captureId = newCaptureId(projectId);
    try {
      setStep({ kind: 'working', phase: 'Preparing the photo…' });
      const prepared = await prepareImage(file);
      await createCapture(projectId, captureId, {
        mode: 'photo',
        instrument: 'unknown',
        capturedByUid: uid,
        capturedByName: user?.displayName ?? user?.email ?? null,
        status: 'pending',
      });
      setStep({ kind: 'working', phase: 'Uploading…' });
      const { storagePath, thumbnailPath, downloadUrl } = await uploadCaptureImage(
        uid,
        captureId,
        prepared.full,
        prepared.thumb,
      );
      await updateDoc(captureDoc(projectId, captureId), { storagePath, thumbnailPath, downloadUrl });

      setStep({ kind: 'working', phase: 'Reading the sheet and learning its columns…' });
      const learned = await learnReaderFromPhoto({
        projectId,
        captureId,
        readerId: newReaderId(uid),
        name: name.trim(),
        storagePath,
      });
      setStep({ kind: 'learned', captureId, learned });
    } catch (e) {
      void rejectCapture(projectId, captureId).catch(() => {});
      setStep({ kind: 'failed', message: (e as Error)?.message ?? 'Could not learn from that photo.' });
    } finally {
      if (cameraRef.current) cameraRef.current.value = '';
      if (uploadRef.current) uploadRef.current.value = '';
    }
  }

  async function save() {
    if (step.kind !== 'learned') return;
    setSaving(true);
    try {
      const reader: ProjectReader = { ...step.learned.reader };
      await saveLearnedReader(uid, reader);
      if (addToProject) await setProjectReaders(projectId, [...chosen, reader]);
      setStep({ kind: 'saved', captureId: step.captureId, learned: step.learned });
    } catch (e) {
      setStep({ kind: 'failed', message: (e as Error)?.message ?? 'Could not save that reader.' });
    } finally {
      setSaving(false);
    }
  }

  const learned = step.kind === 'learned' || step.kind === 'saved' ? step.learned : null;
  const savedCaptureId = step.kind === 'saved' ? step.captureId : null;
  const samples = learned ? new Set(learned.extraction.readings.map((r) => r.sampleLabel)).size : 0;
  const summary = learned?.extraction.dataShape === 'summary';

  return (
    <Modal open={open} onClose={close} title="New reader">
      {step.kind === 'name' && (
        <div className="space-y-4">
          <div>
            <label className="field-label mb-1.5 block" htmlFor="nr-name">
              Name
            </label>
            <Input
              id="nr-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder=""
              autoFocus
            />
          </div>

          <div className="rounded-lg border border-ink-700 bg-ink-800/40 p-3 text-sm text-ink-300">
            <p className="font-medium text-ink-100">Then take a trial photo of a real result</p>
            <p className="mt-1 text-ink-400">
              BlisxBench reads it to learn this reader&apos;s columns — nothing else to fill in.
              Photograph either:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-400">
              <li>
                <span className="text-ink-200">Raw data</span> — every measurement, with no averages
                or SD, or
              </li>
              <li>
                <span className="text-ink-200">A summary</span> — averages with SD, and the number of
                measurements (N) if the sheet shows it.
              </li>
            </ul>
            <p className="mt-2 text-ink-400">
              Keep the column headings and sample names in the shot. The readings in this photo are
              kept for you to save afterwards.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="primary"
              icon={<Camera size={16} />}
              disabled={!name.trim()}
              onClick={() => void openCamera()}
            >
              Take photo
            </Button>
            <Button
              variant="secondary"
              icon={<ImageUp size={16} />}
              disabled={!name.trim()}
              onClick={() => uploadRef.current?.click()}
            >
              Upload image
            </Button>
          </div>
          <p className="text-xs text-ink-500">Learning from a photo uses the same credits as reading one.</p>
        </div>
      )}

      {step.kind === 'working' && (
        <div className="flex flex-col items-center gap-3 py-10 text-center text-sm text-ink-300">
          <Loader2 size={24} className="animate-spin text-accent-400" />
          {step.phase}
          <span className="text-xs text-ink-500">This can take up to a minute.</span>
        </div>
      )}

      {step.kind === 'failed' && (
        <div className="space-y-4">
          <p className="flex gap-2 rounded-lg border border-amber-800 bg-amber-950 p-3 text-sm text-amber-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{step.message}</span>
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setStep({ kind: 'name' })}>
              Try another photo
            </Button>
          </div>
        </div>
      )}

      {learned && (
        <div className="space-y-4">
          {step.kind === 'saved' && (
            <p className="flex items-center gap-2 text-sm text-emerald-300">
              <CheckCircle2 size={16} />
              {learned.reader.name} is ready{addToProject ? ' and added to this project' : ''}.
            </p>
          )}

          <div>
            <p className="field-label mb-1.5">What it reads</p>
            <p className="flex flex-wrap gap-1.5">
              {learned.reader.fields.map((f) => (
                <span
                  key={f.key}
                  className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[11px] text-ink-300"
                >
                  {f.label}
                  {f.unit && <span className="text-ink-500"> {f.unit}</span>}
                </span>
              ))}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge tone="info">{summary ? 'Averages with SD' : 'Individual readings'}</Badge>
            <Badge tone="neutral">
              {samples} sample{samples === 1 ? '' : 's'}
            </Badge>
            <Badge tone={learned.extraction.confidence === 'low' ? 'bad' : 'neutral'}>
              {learned.extraction.confidence} confidence
            </Badge>
          </div>

          {summary && !learned.nDetected && (
            <p className="flex gap-2 rounded-lg border border-amber-800 bg-amber-950 p-2.5 text-xs text-amber-200">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                N — how many measurements each average is from — is not on this sheet. You will be
                asked to type it in whenever you save averages from it.
              </span>
            </p>
          )}

          {step.kind === 'learned' ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={close} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                disabled={saving}
                onClick={() => {
                  void rejectCapture(projectId, step.captureId).catch(() => {});
                  setStep({ kind: 'name' });
                }}
              >
                Try another photo
              </Button>
              <Button variant="primary" loading={saving} onClick={() => void save()}>
                Save reader
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-ink-300">
                The trial photo holds {learned.extraction.readings.length} reading
                {learned.extraction.readings.length === 1 ? '' : 's'} from {samples} sample
                {samples === 1 ? '' : 's'}. They are not saved yet — review them now, or later from
                the project page.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  Later
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    reset();
                    onClose();
                    if (savedCaptureId) onReview(savedCaptureId, learned);
                  }}
                >
                  Review and save now
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void learnFrom(file);
        }}
      />
      <input
        ref={uploadRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void learnFrom(file);
        }}
      />
    </Modal>
  );
}
